/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Dimensions, Image, View } from 'react-native';
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import { pd } from '@airasia-rn/ui';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { getTextStyle } from '@airasia-phoenix/react-native/utils';
import type { SocialFeedAnimationViewProps } from './types';
import { useTranslation } from 'react-i18next';

const DEFAULT_IMAGE_1 =
  'https://static.airasia.com/design/images/feed/beach.png';
const DEFAULT_IMAGE_2 =
  'https://static.airasia.com/design/images/feed/flora.png';
const DEFAULT_IMAGE_3 =
  'https://static.airasia.com/design/images/feed/sundae.png';

const DEFAULT_IMAGES = [
  { uri: DEFAULT_IMAGE_1 },
  { uri: DEFAULT_IMAGE_2 },
  { uri: DEFAULT_IMAGE_3 },
] as const;

const CARD_BORDER_RADIUS = 12;
const CONTAINER_HEIGHT = 140;
const SCREEN_WIDTH = Dimensions.get('window').width;

// Text block final position (left-aligned with padding)
const TEXT_PADDING_LEFT = 16;
const TEXT_BLOCK_WIDTH = 160;

// Initial overlapping cluster layout
const CLUSTER_HEIGHT = 90;
const CARD_SIZES = [40, 56, 64] as const;
const CARD_ROTATIONS = [-40, -10, 30] as const;
const CARD_LEFT_OFFSETS = [0, 26, 74] as const;
const CARD_TOP_OFFSETS = [
  CLUSTER_HEIGHT - CARD_SIZES[0],
  (CLUSTER_HEIGHT - CARD_SIZES[1]) / 1.8,
  CARD_SIZES[2] / 4,
] as const;

// Width of the cluster in its initial stacked state
const CLUSTER_WIDTH = 160;

// Phase 2 target
const FINAL_CARD_HEIGHT = 200;
const FINAL_PADDING_HORIZONTAL = 16;
const FINAL_AVAILABLE_WIDTH = SCREEN_WIDTH - FINAL_PADDING_HORIZONTAL * 2;
const FINAL_CARD_WIDTH = FINAL_AVAILABLE_WIDTH / 3;

// Animation timing
// Total sequence: 500ms delay → 500ms Phase 1 → 500ms hold → 1000ms Phase 2 = 2500ms
const DEFAULT_START_DELAY_MS = 500;
const DEFAULT_PHASE2_DELAY_MS = 500;
const PHASE1_DURATION_MS = 500;
const PHASE2_DURATION_MS = 1000;

const EASING = Easing.out(Easing.cubic);

export const SocialFeedAnimationView = ({
  onAnimationEnd,
  onPhaseTwo,
  startAnimation = true,
  startDelay = DEFAULT_START_DELAY_MS,
  phase2Delay = DEFAULT_PHASE2_DELAY_MS,
  images,
}: SocialFeedAnimationViewProps) => {
  const { t } = useTranslation('social');

  // Track which remote images have loaded
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  // Remote image URIs for loading
  const remoteImageUris = useMemo(
    () => [0, 1, 2].map((i) => images?.[i as 0 | 1 | 2]),
    [images],
  );

  const handleImageLoad = useCallback((index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  }, []);

  // Image cluster final resting: right-aligned with TEXT_PADDING_LEFT gap from edge
  const clusterFinalLeft = SCREEN_WIDTH / 2;

  // Phase 1: 0 = both at screen edges, 1 = both at center
  const entranceProgress = useSharedValue(0);
  // Phase 2: 0 = resting, 1 = expanded final state
  const transitionProgress = useSharedValue(0);

  const startPhaseTwo = useCallback(() => {
    runOnJS(onPhaseTwo ?? (() => {}))();
    transitionProgress.value = withDelay(
      phase2Delay,
      withTiming(
        1,
        { duration: PHASE2_DURATION_MS, easing: EASING },
        (finished) => {
          if (finished) {
            runOnJS(onAnimationEnd ?? (() => {}))();
          }
        },
      ),
    );
  }, [onPhaseTwo, onAnimationEnd, transitionProgress, phase2Delay]);

  useEffect(() => {
    if (!startAnimation) return;

    entranceProgress.value = withDelay(
      startDelay,
      withTiming(
        1,
        { duration: PHASE1_DURATION_MS, easing: EASING },
        (finished) => {
          if (finished) runOnJS(startPhaseTwo)();
        },
      ),
    );
  }, [startAnimation, startDelay, entranceProgress, startPhaseTwo]);

  // ─── Text Container ─────────────────────────────────────────────────────────
  // Starts visible at the left screen edge (left: 0), slides right to meet center.
  // Phase 2: drifts upward and fades out.
  const textAnimatedStyle = useAnimatedStyle(() => {
    const left = interpolate(
      entranceProgress.value,
      [0, 1],
      [TEXT_PADDING_LEFT, SCREEN_WIDTH / 6],
      Extrapolation.CLAMP,
    );
    const translateY = interpolate(
      transitionProgress.value,
      [0, 1],
      [0, -80],
      Extrapolation.CLAMP,
    );
    const opacity = interpolate(
      transitionProgress.value,
      [0, 0.5, 1],
      [1, 0.3, 0],
      Extrapolation.CLAMP,
    );
    return { left, transform: [{ translateY }], opacity };
  }, [entranceProgress, transitionProgress]);

  // ─── Image Cluster Container ─────────────────────────────────────────────────
  // Starts visible at the right screen edge, slides left to meet center.
  // Phase 2: left animates to 0 so cards expand flush from the left edge.
  const clusterStartLeft = SCREEN_WIDTH - CLUSTER_WIDTH - TEXT_PADDING_LEFT;

  const imageClusterAnimatedStyle = useAnimatedStyle(() => {
    const left = interpolate(
      entranceProgress.value,
      [0, 1],
      [clusterStartLeft, clusterFinalLeft],
      Extrapolation.CLAMP,
    );
    const phase2Left = interpolate(
      transitionProgress.value,
      [0, 1],
      [clusterFinalLeft, FINAL_PADDING_HORIZONTAL],
      Extrapolation.CLAMP,
    );
    const finalLeft = transitionProgress.value > 0 ? phase2Left : left;

    // Arc effect: dips 20px upward just before reaching the text container.
    // Peaks at 80% of the entrance, fully settled by 100%.
    const translateY = interpolate(
      entranceProgress.value,
      [0, 1, 1],
      [0, -20, 0],
      Extrapolation.CLAMP,
    );

    return { left: finalLeft, transform: [{ translateY }] };
  }, [entranceProgress, transitionProgress]);

  // ─── Per-Card Animated Styles ────────────────────────────────────────────────
  const card0Style = useAnimatedStyle(() => {
    const left = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_LEFT_OFFSETS[0], 0],
      Extrapolation.CLAMP,
    );
    const top = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_TOP_OFFSETS[0], 0],
      Extrapolation.CLAMP,
    );
    const size = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[0], FINAL_CARD_WIDTH],
      Extrapolation.CLAMP,
    );
    const height = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[0], FINAL_CARD_HEIGHT],
      Extrapolation.CLAMP,
    );
    const rotateDeg = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_ROTATIONS[0], 0],
      Extrapolation.CLAMP,
    );
    return {
      left,
      top,
      width: size,
      height,
      transform: [{ rotate: `${rotateDeg}deg` }],
    };
  }, [transitionProgress]);

  const card1Style = useAnimatedStyle(() => {
    const left = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_LEFT_OFFSETS[1], FINAL_CARD_WIDTH],
      Extrapolation.CLAMP,
    );
    const top = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_TOP_OFFSETS[1], 0],
      Extrapolation.CLAMP,
    );
    const size = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[1], FINAL_CARD_WIDTH],
      Extrapolation.CLAMP,
    );
    const height = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[1], FINAL_CARD_HEIGHT],
      Extrapolation.CLAMP,
    );
    const rotateDeg = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_ROTATIONS[1], 0],
      Extrapolation.CLAMP,
    );
    return {
      left,
      top,
      width: size,
      height,
      transform: [{ rotate: `${rotateDeg}deg` }],
    };
  }, [transitionProgress]);

  const card2Style = useAnimatedStyle(() => {
    const left = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_LEFT_OFFSETS[2], 2 * FINAL_CARD_WIDTH],
      Extrapolation.CLAMP,
    );
    const top = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_TOP_OFFSETS[2], 0],
      Extrapolation.CLAMP,
    );
    const size = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[2], FINAL_CARD_WIDTH],
      Extrapolation.CLAMP,
    );
    const height = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_SIZES[2], FINAL_CARD_HEIGHT],
      Extrapolation.CLAMP,
    );
    const rotateDeg = interpolate(
      transitionProgress.value,
      [0, 1],
      [CARD_ROTATIONS[2], 0],
      Extrapolation.CLAMP,
    );
    return {
      left,
      top,
      width: size,
      height,
      transform: [{ rotate: `${rotateDeg}deg` }],
    };
  }, [transitionProgress]);

  const cardStyles = [card0Style, card1Style, card2Style];

  const clusterHeightStyle = useAnimatedStyle(() => {
    const height = interpolate(
      transitionProgress.value,
      [0, 1],
      [CLUSTER_HEIGHT, FINAL_CARD_HEIGHT],
      Extrapolation.CLAMP,
    );
    return { height };
  }, [transitionProgress]);

  return (
    // Clip wrapper — exact screenWidth so translateX/left values outside this
    // boundary are hidden, ensuring both containers are invisible before animating.
    <View style={[styles.clipWrapper, { width: SCREEN_WIDTH }]}>
      <View style={styles.container}>
        {/* Text — absolutely positioned, starts off the left edge */}
        <Animated.View style={[styles.textBlock, textAnimatedStyle]}>
          <TextView
            text={t('movement.animationTitle')}
            numberOfLines={1}
            style={[
              getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.content_primary,
              ),
              styles.title,
            ]}
          />
          <TextView
            text={t('movement.animationDescription')}
            numberOfLines={2}
            style={[
              getTextStyle(
                pd.sys.typescale.heading_4,
                pd.sys.colour.content_secondary,
              ),
              styles.subtitle,
            ]}
          />
        </Animated.View>

        {/* Image cluster — absolutely positioned, starts off the right edge */}
        <Animated.View style={[styles.imageCluster, imageClusterAnimatedStyle]}>
          <Animated.View style={[styles.imageClusterInner, clusterHeightStyle]}>
            {[0, 1, 2].map((index) => {
              const remoteUri = remoteImageUris[index];
              const hasRemoteImage = !!remoteUri;
              const isLoaded = loadedImages.has(index);

              return (
                <Animated.View
                  key={index}
                  style={[styles.card, cardStyles[index]]}
                >
                  {/* Placeholder/default image - always visible as background */}
                  <Image
                    source={DEFAULT_IMAGES[index]}
                    resizeMode="cover"
                    style={styles.cardImage}
                  />
                  {/* Remote image - overlay when loaded */}
                  {hasRemoteImage && (
                    <Image
                      source={{ uri: remoteUri }}
                      resizeMode="cover"
                      style={[
                        styles.cardImage,
                        styles.cardImageOverlay,
                        isLoaded
                          ? styles.cardImageVisible
                          : styles.cardImageHidden,
                      ]}
                      onLoad={() => handleImageLoad(index)}
                    />
                  )}
                </Animated.View>
              );
            })}
          </Animated.View>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  // Full-screen-width wrapper — no clipping so both containers are visible
  // at the screen edges at the start of the animation.
  clipWrapper: {
    height: CONTAINER_HEIGHT,
  },
  // Inner container — same dimensions, both children absolutely positioned.
  container: {
    width: '100%' as const,
    height: CONTAINER_HEIGHT,
    position: 'relative' as const,
  },
  // Text block — absolutely positioned, `left` is animated from off-screen.
  textBlock: {
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    width: TEXT_BLOCK_WIDTH,
    justifyContent: 'center' as const,
    alignItems: 'flex-start' as const,
  },
  title: {
    textAlign: 'left' as const,
  },
  subtitle: {
    marginTop: 4,
    textAlign: 'left' as const,
  },
  // Image cluster — absolutely positioned, `left` is animated from off-screen.
  // Width is the final available width so cards never exceed the padded boundary.
  imageCluster: {
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    width: FINAL_AVAILABLE_WIDTH,
    height: 180,
    justifyContent: 'center' as const,
  },
  imageClusterInner: {
    position: 'relative' as const,
    width: CLUSTER_WIDTH,
  },
  card: {
    position: 'absolute' as const,
    overflow: 'hidden' as const,
    borderRadius: CARD_BORDER_RADIUS,
    backgroundColor: pd.sys.colour.container_low.toString(),
  },
  cardImage: {
    width: '100%',
    height: '100%',
  },
  cardImageOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  cardImageVisible: {
    opacity: 1,
  },
  cardImageHidden: {
    opacity: 0,
  },
}));
